import type { TFunction } from 'i18next';
import { text, type FieldDef, type FieldError } from '../types';
import { formatSummaryDate, type RuleSummarizer } from '../summary';

/**
 * Localized message of an engine error code for a field. `summarizer` words a broken validation rule that has no
 * message of its own.
 */
export function errorMessage(t: TFunction, field: FieldDef | undefined, error: FieldError, lang: 'sk' | 'en', summarizer?: RuleSummarizer): string {
  const label = field ? text(field.label, lang) : error.field;
  const p = error.params ?? {};
  const date = (v: unknown) => (typeof v === 'string' ? formatSummaryDate(v, lang) : '');
  switch (error.code) {
    case 'required':
      switch (field?.type) {
        case 'DATE':
          return t('form.errors.requiredDate', { label });
        case 'SINGLE_CHOICE':
        case 'YES_NO':
        case 'TEAM_MEMBER':
        case 'AUTOCOMPLETE':
          return t('form.errors.requiredChoice', { label });
        case 'MULTI_CHOICE':
          return t('form.errors.requiredMulti', { label });
        case 'REPEATING_GROUP':
          return t('form.errors.requiredRows', { label });
        case 'TABLE':
          return t('form.errors.requiredTable', { label });
        default:
          return t('form.errors.required', { label });
      }
    case 'maxLength':
      // a table names the cell that is too long
      if (field?.type === 'TABLE' && typeof p.row === 'string' && typeof p.column === 'string') {
        return t('form.errors.tableMaxLength', { label, cell: cellName(field, p.row, p.column, lang), max: p.max, count: Number(p.max) });
      }
      return t('form.errors.maxLength', { label, max: p.max, count: Number(p.max) });
    case 'table.incomplete':
      return t('form.errors.tableIncomplete', { label });
    case 'rule.invalid':
      // the message of the field's validation rule (D26), else the rule in words
      if (field?.validationMessage) return text(field.validationMessage, lang);
      return t('form.errors.ruleInvalid', { label, condition: field?.validWhen && summarizer ? summarizer.text(field.validWhen, lang, false) : '' });
    case 'number.min':
      return t('form.errors.numberMin', { label, min: lang === 'sk' ? String(p.min).replace('.', ',') : p.min });
    case 'number.max':
      return t('form.errors.numberMax', { label, max: lang === 'sk' ? String(p.max).replace('.', ',') : p.max });
    case 'number.decimals':
      return t('form.errors.numberDecimals', { label, count: Number(p.decimals) });
    case 'date.min':
      return t('form.errors.dateMin', { label, min: date(p.min) });
    case 'date.max':
      return t('form.errors.dateMax', { label, max: date(p.max) });
    case 'date.timeframe':
      return t('form.errors.dateTimeframe', { label, from: date(p.from), to: date(p.to) });
    case 'date.onOrBeforeStart':
      return t('form.errors.dateOnOrBeforeStart', { label, from: date(p.from) });
    case 'date.onOrAfterToday':
      return t('form.errors.dateOnOrAfterToday', { label });
    case 'rows.min':
      return t('form.errors.rowsMin', { label, count: Number(p.min) });
    case 'rows.max':
      return t('form.errors.rowsMax', { label, count: Number(p.max) });
    case 'choice.minSelected':
      return t('form.errors.minSelected', { label, count: Number(p.min) });
    case 'choice.maxSelected':
      return t('form.errors.maxSelected', { label, count: Number(p.max) });
    default:
      return t(`form.errors.${error.code.replace('.', '_')}`, { ...p, label, defaultValue: t('form.errors.invalid', { label }) });
  }
}

/** "Chlapci, 1. ročník": the row header and the column header of a cell of a TABLE. */
export function cellName(field: FieldDef, row: string, column: string, lang: 'sk' | 'en'): string {
  const r = field.table?.rows.find((x) => x.key === row);
  const c = field.table?.columns.find((x) => x.key === column);
  return [r ? text(r.label, lang) : row, c ? text(c.label, lang) : column].join(', ');
}

/** "plannedActivities[1].activityDate" → { group: "plannedActivities", row: 1, key: "activityDate" }. */
export function parsePath(path: string): { group?: string; row?: number; key: string } {
  const m = /^([^[\]]+)\[(\d+)\]\.(.+)$/.exec(path);
  return m ? { group: m[1]!, row: Number(m[2]), key: m[3]! } : { key: path };
}

/** DOM id of a field: {formType}-{key}, with -{row} inside repeating rows. */
export function fieldId(formType: string, key: string, row?: number): string {
  return row === undefined ? `${formType}-${key}` : `${formType}-${key}-${row}`;
}

/** DOM id of the input of a TABLE cell: {fieldId}-{rowKey}-{columnKey}. */
export function cellId(base: string, row: string, column: string): string {
  return `${base}-${row}-${column}`;
}

/**
 * The element an error-summary link targets: first date part, first option, the cell of a table the error is about
 * (else its first cell), or the control itself.
 */
export function errorTargetId(formType: string, field: FieldDef | undefined, path: string, params?: Record<string, unknown>): string {
  const { row, key } = parsePath(path);
  const base = fieldId(formType, key, row);
  if (field?.type === 'DATE') return `${base}-day`;
  if (field?.type === 'REPEATING_GROUP') return `${base}-add`;
  if (field?.type === 'TABLE') {
    const first = { row: field.table?.rows[0]?.key, column: field.table?.columns[0]?.key };
    const cell = typeof params?.row === 'string' && typeof params.column === 'string' ? { row: params.row, column: params.column } : first;
    return cell.row && cell.column ? cellId(base, cell.row, cell.column) : base;
  }
  return base;
}
